import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceArea,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { useDarkMode } from '../../context/DarkModeContext';

function fmt(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

// ── Nice tick computation for Y axis ────────────────────────────────
function computeYTicks(min, max, targetCount = 8) {
  const range = max - min || 1;
  const rough = range / targetCount;
  const mag   = Math.pow(10, Math.floor(Math.log10(rough)));
  const norm  = rough / mag;
  const nice  = norm < 1.5 ? 1 : norm < 3 ? 2 : norm < 7 ? 5 : 10;
  const step  = nice * mag;
  const start = Math.floor(min / step) * step;
  const ticks = [];
  for (let t = start; t <= max + step * 0.1; t += step) ticks.push(parseFloat(t.toFixed(4)));
  return ticks;
}

// ── Tooltip ──────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  if (!d) return null;

  const isFirstDay = d.T_val === 0 && d.D_val === 0 && d.G_val === 0;
  const net = d.T_val + d.D_val + d.G_val + d.R_val;

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 shadow-lg text-xs min-w-[210px]">
      <p className="font-semibold text-gray-700 dark:text-gray-200 mb-1.5">{fmt(label)}</p>
      <div className="flex justify-between gap-3 mb-1">
        <span className="text-gray-400">Prev Close:</span>
        <span className="font-medium text-gray-500">
          {isFirstDay ? '— (first day)' : `₹${d.prev_close?.toFixed(2)}`}
        </span>
      </div>
      {!isFirstDay && (
        <>
          <div className="flex items-center gap-2 mb-0.5">
            <span className="w-2.5 h-2.5 rounded-sm bg-red-500 flex-shrink-0" />
            <span className="text-gray-500">T — Time Decay:</span>
            <span className="font-medium text-red-500 ml-auto">₹{d.T_val.toFixed(2)}</span>
          </div>
          <div className="flex items-center gap-2 mb-0.5">
            <span className="w-2.5 h-2.5 rounded-sm bg-blue-500 flex-shrink-0" />
            <span className="text-gray-500">D — Directional:</span>
            <span className="font-medium text-blue-500 ml-auto">₹{d.D_val.toFixed(2)}</span>
          </div>
          <div className="flex items-center gap-2 mb-0.5">
            <span className="w-2.5 h-2.5 rounded-sm bg-green-500 flex-shrink-0" />
            <span className="text-gray-500">G — Convexity:</span>
            <span className="font-medium text-green-500 ml-auto">₹{d.G_val.toFixed(2)}</span>
          </div>
          {Math.abs(d.R_val) > 0.005 && (
            <div className="flex items-center gap-2 mb-0.5">
              <span className="w-2.5 h-2.5 rounded-sm bg-violet-400 flex-shrink-0" />
              <span className="text-gray-500">R — Residual (IV):</span>
              <span className="font-medium text-violet-400 ml-auto">₹{d.R_val.toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between mt-1.5 pt-1.5 border-t border-gray-200 dark:border-gray-600">
            <span className="text-gray-500">Net Change:</span>
            <span className={`font-semibold ${net >= 0 ? 'text-green-600' : 'text-red-500'}`}>
              {net >= 0 ? '+' : ''}₹{net.toFixed(2)}
            </span>
          </div>
        </>
      )}
      <div className="flex justify-between mt-0.5">
        <span className="text-gray-500">Close:</span>
        <span className="font-semibold text-amber-500">₹{d.option_close?.toFixed(2)}</span>
      </div>
    </div>
  );
};

// ── Main chart ────────────────────────────────────────────────────────
export default function PDEStackedChart({ data, height = 280, syncId }) {
  const { dark } = useDarkMode();

  if (!data || data.length === 0) return null;

  const raw = data.filter((d) => d.theta != null && d.delta != null && d.gamma != null);
  if (raw.length === 0) return null;

  // Build per-day floating range bars.
  // Each component bar is [lower, upper] so it floats at the right waterfall level.
  // T_bar starts at prev_close; D_bar starts where T_bar ends; etc.
  // R (residual) closes the gap to actual option_close, so the amber line always
  // aligns with the top (or bottom) of the last bar.
  const chartData = raw.map((d, i, arr) => {
    if (i === 0) {
      return {
        ...d,
        prev_close: d.option_close,
        T_bar: null, D_bar: null, G_bar: null, R_bar: null,
        T_val: 0, D_val: 0, G_val: 0, R_val: 0,
      };
    }
    const prev  = arr[i - 1];
    const dS    = (d.spot_price ?? 0) - (prev.spot_price ?? 0);
    const T     = prev.theta;
    const D     = prev.delta * dS;
    const G     = 0.5 * prev.gamma * dS * dS;
    const actual = (d.option_close ?? 0) - (prev.option_close ?? 0);
    const R     = actual - (T + D + G);

    const l0 = prev.option_close ?? 0;
    const l1 = l0 + T;
    const l2 = l1 + D;
    const l3 = l2 + G;
    const l4 = l3 + R; // === d.option_close

    return {
      ...d,
      prev_close: l0,
      T_val: T, D_val: D, G_val: G, R_val: R,
      T_bar: [Math.min(l0, l1), Math.max(l0, l1)],
      D_bar: [Math.min(l1, l2), Math.max(l1, l2)],
      G_bar: [Math.min(l2, l3), Math.max(l2, l3)],
      R_bar: [Math.min(l3, l4), Math.max(l3, l4)],
    };
  });

  // Y domain from all option prices
  const allPrices = raw.map((d) => d.option_close).filter((v) => v != null);
  const yMin   = Math.min(...allPrices);
  const yMax   = Math.max(...allPrices);
  const yTicks = computeYTicks(yMin, yMax);
  const domainMin = yTicks[0];
  const domainMax = yTicks[yTicks.length - 1];

  const axis     = dark ? '#6b7280' : '#9ca3af';
  const gridV    = dark ? '#374151' : '#e5e7eb';
  const stripOdd = dark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)';

  const legendMap = {
    T_bar: 'T — Time Decay',
    D_bar: 'D — Directional',
    G_bar: 'G — Convexity',
    R_bar: 'R — Residual (IV)',
    option_close: 'Option Close',
  };

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart
        data={chartData}
        syncId={syncId}
        margin={{ top: 8, right: 12, bottom: 24, left: 8 }}
        barCategoryGap="10%"
        barGap={1}
      >
        {/* Alternating X-axis column bands */}
        {chartData.map((entry, i) =>
          i % 2 === 0 ? (
            <ReferenceArea
              key={`col-${i}`}
              x1={entry.date}
              x2={entry.date}
              fill={stripOdd}
              stroke="none"
            />
          ) : null
        )}

        {/* Horizontal grid lines only (vertical replaced by column stripes) */}
        <CartesianGrid vertical={false} horizontal stroke={gridV} strokeDasharray="3 3" />

        <XAxis
          dataKey="date"
          tickFormatter={fmt}
          tick={{ fontSize: 10, fill: axis }}
          stroke={axis}
          angle={-30}
          textAnchor="end"
          height={44}
        />
        <YAxis
          ticks={yTicks}
          tickFormatter={(v) => `₹${v.toFixed(0)}`}
          tick={{ fontSize: 10, fill: axis }}
          stroke={axis}
          width={48}
          domain={[domainMin, domainMax]}
        />

        <Tooltip content={<CustomTooltip />} />
        <Legend
          wrapperStyle={{ fontSize: 11, paddingTop: 6 }}
          formatter={(value) => legendMap[value] ?? value}
        />

        {/* T — Time Decay (always negative → red outline) */}
        <Bar dataKey="T_bar" name="T_bar" fill="#ef4444" isAnimationActive={false} maxBarSize={20}>
          {chartData.map((entry, i) => (
            <Cell key={i} fill="#ef4444" fillOpacity={entry.T_bar ? 0.88 : 0}
              stroke={entry.T_bar ? '#dc2626' : 'none'} strokeWidth={1.5} />
          ))}
        </Bar>

        {/* D — Directional */}
        <Bar dataKey="D_bar" name="D_bar" fill="#3b82f6" isAnimationActive={false} maxBarSize={20}>
          {chartData.map((entry, i) => (
            <Cell key={i} fill="#3b82f6" fillOpacity={entry.D_bar ? 0.88 : 0}
              stroke={entry.D_bar ? (entry.D_val >= 0 ? '#16a34a' : '#dc2626') : 'none'} strokeWidth={1.5} />
          ))}
        </Bar>

        {/* G — Convexity (always positive → green outline) */}
        <Bar dataKey="G_bar" name="G_bar" fill="#22c55e" isAnimationActive={false} maxBarSize={20}>
          {chartData.map((entry, i) => (
            <Cell key={i} fill="#22c55e" fillOpacity={entry.G_bar ? 0.88 : 0}
              stroke={entry.G_bar ? '#16a34a' : 'none'} strokeWidth={1.5} />
          ))}
        </Bar>

        {/* R — Residual */}
        <Bar dataKey="R_bar" name="R_bar" fill="#a78bfa" isAnimationActive={false} maxBarSize={20}>
          {chartData.map((entry, i) => (
            <Cell key={i} fill="#a78bfa" fillOpacity={entry.R_bar ? 0.80 : 0}
              stroke={entry.R_bar ? (entry.R_val >= 0 ? '#16a34a' : '#dc2626') : 'none'} strokeWidth={1.5} />
          ))}
        </Bar>

        {/* Amber line tracing actual option close */}
        <Line
          type="monotone"
          dataKey="option_close"
          name="option_close"
          stroke="#f59e0b"
          strokeWidth={2}
          dot={({ cx, cy, key }) => (
            <g key={key}>
              <line x1={cx - 4} y1={cy - 4} x2={cx + 4} y2={cy + 4} stroke="#f59e0b" strokeWidth={2} />
              <line x1={cx + 4} y1={cy - 4} x2={cx - 4} y2={cy + 4} stroke="#f59e0b" strokeWidth={2} />
            </g>
          )}
          activeDot={{ r: 4 }}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
}
